<!-- @format -->
<template>
    <div class="journey-template-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Journey Template Settings</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="loading">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <div class="journey-name-container">
                        <div class="journey-name-label">Name</div>
                        <app-input
                            v-model="template.name"
                            placeholder="Name"
                            :is-valid="!submitted || $v.template.name.required"
                            error="Name is required"
                        />
                    </div>
                    <div class="categories-container">
                        <global-category-dropdown
                            :account-id="accountId"
                            :selected="template.categories"
                            :show-selected-up="true"
                            @input="addCategory"
                            @remove="deleteCategory"
                        />
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button class="save-btn" @click="saveTemplate()"> Save </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');

import { JourneyService } from '@/scripts/services/journey/journey-service';

import EventBus from '@/scripts/event-bus.js';
import { required } from 'vuelidate/lib/validators';
import GlobalCategoryDropdown from '../common/global-category-dropdown.vue';

export default {
    name: 'journey-template-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'global-category-dropdown': GlobalCategoryDropdown,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            submitted: false,
            accountId: this.widget.accountId,
            clientId: this.widget.clientId,
            agencyId: this.widget.agencyId,
            duplicateTemplateId: this.widget.duplicateJourneyTemplateId,
            loading: true,

            template: {
                id: this.widget.journeyTemplateId,
                name: null,
                categories: [],
            },
        };
    },

    validations: {
        template: {
            name: {
                required,
            },
        },
    },

    created() {
        if (this.template.id) {
            this.getTemplate(this.template.id);
        } else if (this.duplicateTemplateId) {
            this.getTemplate(this.duplicateTemplateId);
        } else {
            this.loading = false;
        }
    },

    methods: {
        addCategory(data) {
            this.template.categories.includes(data.name)
                ? ''
                : this.template.categories.push(data.name);
        },

        deleteCategory(name) {
            this.template.categories = this.template.categories.filter((category) => {
                return name != category;
            });
        },

        saveTemplate() {
            this.submitted = true;

            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            this.loading = true;

            if (this.template.id) {
                this.patchTemplate()
                    .then(() => {
                        EventBus.$emit('journey-template:update');
                        EventBus.$emit('my-creations:update');
                        EventBus.$emit('journey-report:update');
                        this.$helpers.showSuccessNotification('Template saved successfully.');
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.loading = false;
                        this.$helpers.showFailNotification(
                            'There was an error saving your template.',
                            { error },
                        );
                    });
            } else if (this.duplicateTemplateId) {
                this.duplicateTemplate();
            } else {
                this.postTemplate()
                    .then(() => {
                        EventBus.$emit('journey-template:update');
                        this.$helpers.showSuccessNotification('Template saved successfully.');
                        this.$router.push(`/JourneyTemplates/${this.template.id}/Design`);
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.loading = false;
                        this.$helpers.showFailNotification(
                            'There was an error saving your template.',
                            { error },
                        );
                    });
            }
        },

        patchTemplate() {
            var changes = [
                {
                    name: 'Name',
                    value: this.template.name,
                },
                {
                    name: 'Categories',
                    value: this.template.categories,
                },
            ];

            return JourneyService.patchTemplate(this.template.id, changes);
        },

        postTemplate() {
            return new Promise((resolve, reject) => {
                var request = {
                    Name: this.template.name,
                    ClientId: this.clientId,
                    AgencyId: this.agencyId,
                    Categories: this.template.categories,
                };

                JourneyService.postTemplate('', request)
                    .then((response) => {
                        this.template.id = response.data.Id;
                        resolve();
                    })
                    .catch(reject);
            });
        },

        duplicateTemplate() {
            var request = {
                Name: this.template.name,
                Categories: this.template.categories,
            };

            JourneyService.postTemplate(`${this.duplicateTemplateId}/Duplicate`, request)
                .then((response) => {
                    EventBus.$emit('journey-template:update');
                    this.$helpers.showSuccessNotification(
                        'Journey template successfully duplicated.'
                    );
                    this.$router.push(`/JourneyTemplates/${response.data.Id}/Design`);
                    this.closeWidget();
                })
                .catch((error) => {
                    this.loading = false;
                    this.$helpers.showFailNotification('Failed to duplicate journey template.', { error });
                });
        },

        getTemplate(templateId) {
            const params = {
                filter: `Id eq ${templateId}`,
                expand: 'Categories($select=Name)',
                select: 'Name',
            };

            JourneyService.getTemplate(params)
                .then((response) => {
                    const data = response.data.value[0];

                    this.template.name = data.Name;

                    if (this.duplicateTemplateId) {
                        this.template.name += ' (Copy)';
                    }

                    this.template.categories = data.Categories.map((category) => category.Name);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to get journey template.', { error });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.journey-template-widget .loading-screen {
    text-align: center;
}

.journey-template-widget .journey-name-container .journey-name-label {
    font-weight: 500;
}

.journey-template-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.journey-template-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.journey-template-widget .journey-name-container {
    margin-bottom: 23px;
}
</style>
